import { parseRawAmount } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import {
  LenderCrossPoolMeta,
  LenderYieldComplete,
  UserData,
} from '../utils/types'
import {
  aaveV4Spokes,
  parseAaveV4SpokeLenderKey,
} from '@1delta/data-sdk'
import { USER_CALLS_PER_RESERVE } from './userCallBuild'
import { createAaveV4UserState } from './createAaveV4UserState'

/**
 * Returns a [converter, expectedCallCount] tuple for Aave V4 user data.
 *
 * Call layout per reserve (4 calls):
 *   0: getUserSuppliedAssets → uint256
 *   1: getUserDebt           → (drawnDebt, premiumDebt)
 *   2: getUserReserveStatus  → (usingAsCollateral, isBorrowing)
 *   3: getUserPosition       → UserPosition struct (incl. dynamicConfigKey)
 *
 * Aave V4 spokes are isolated risk silos (each spoke has its own health
 * factor and risk premium, no cross-margining). The fetcher operates on
 * **one spoke per call** — the lender argument is a per-spoke synthesized
 * key (`AAVE_V4_<HEX>`) and the converter returns a single `UserData`,
 * exactly like every other single-market lender.
 */
export const getAaveV4UserDataConverter = (
  /** Per-spoke synthesized lender key (`AAVE_V4_<HEX>`) */
  lender: string,
  chainId: string,
  account: string,
  /**
   * The lender's slice of the cross-pool meta map (marketUid → meta).
   * Same convention as Morpho/Aave V3 — pre-filtered to this lender by
   * the dispatcher.
   */
  meta?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  const parsed = parseAaveV4SpokeLenderKey(lender)
  const spokeEntry = parsed
    ? aaveV4Spokes()?.[chainId]?.[parsed.spokeAddrLower]
    : undefined

  if (!spokeEntry) {
    return [() => undefined, 0]
  }

  const spokeAddr = spokeEntry.spoke
  const spokeAddrLc = spokeAddr.toLowerCase()
  const reserves = spokeEntry.reserves ?? []

  const flatMeta: LenderCrossPoolMeta = (meta ?? {}) as LenderCrossPoolMeta

  const expectedNumberOfCalls = reserves.length * USER_CALLS_PER_RESERVE

  return [
    (data: any[]) => {
      if (data.length !== expectedNumberOfCalls) {
        return undefined
      }

      const lendingPositions: { [marketUid: string]: any } = {}
      let totalDeposits24h = 0
      let totalDebt24h = 0

      for (let i = 0; i < reserves.length; i++) {
        const r = reserves[i]
        const underlying = (r.underlying ?? '').toLowerCase()
        if (!underlying) continue

        // marketUid leaf is reserveId (not underlying) — see normalize.ts.
        // Format: `AAVE_V4_0x<spoke>:<chainId>:<reserveId>`. The same
        // underlying can appear twice in a spoke via different hubs, so
        // reserveId is the only stable per-reserve identifier.
        const key = createMarketUid(chainId, lender, String(r.reserveId))
        const metaEntity = flatMeta[key]
        if (!metaEntity) continue

        const base = i * USER_CALLS_PER_RESERVE

        // Extract user's pinned dynamicConfigKey from getUserPosition (4th call)
        const userPositionResult = data[base + 3]
        const userDynConfigKey = Number(
          userPositionResult?.dynamicConfigKey ?? 0,
        )

        const { dataForAsset, addedDebt, addedDeposits } = createAaveV4Entry(
          base,
          data,
          key,
          metaEntity,
          spokeAddrLc,
        )
        if (!dataForAsset) continue

        ;(dataForAsset as any).userConfigKey = String(userDynConfigKey)

        totalDebt24h += addedDebt
        totalDeposits24h += addedDeposits
        lendingPositions[key] = dataForAsset
      }

      const payload = {
        chainId,
        account,
        lendingPositions,
        rewards: [],
        userEMode: 0,
      }
      return createAaveV4UserState(
        payload,
        flatMeta,
        totalDeposits24h,
        totalDebt24h,
      )
    },
    expectedNumberOfCalls,
  ]
}

/**
 * Get the config-scoped oracle price for a V4 reserve.
 *
 * The meta endpoint provides `oraclePrice.byConfig` keyed by spoke address,
 * since each spoke has its own oracle. Falls back to the global oracle price.
 */
function getConfigOraclePrice(
  meta: LenderYieldComplete,
  spokeAddr?: string,
): number {
  if (spokeAddr) {
    const byConfig = (meta as any)?.oraclePrice?.byConfig
    const configPrice = byConfig?.[spokeAddr]?.oraclePriceUsd
    if (configPrice != null && configPrice > 0) return configPrice
  }
  return getOraclePrice(meta)
}

function createAaveV4Entry(
  base: number,
  data: any[],
  key: string,
  meta: LenderYieldComplete,
  spokeAddr?: string,
) {
  const rawSupply = data[base]
  const debtResult = data[base + 1]
  const statusResult = data[base + 2]

  // Guard against failed multicall results ('0x')
  const suppliedRaw =
    rawSupply === '0x' || rawSupply === undefined ? '0' : rawSupply.toString()

  // getUserDebt returns (drawnDebt, premiumDebt)
  const drawnDebtRaw = Array.isArray(debtResult)
    ? (debtResult[0]?.toString() ?? '0')
    : '0'
  const premiumDebtRaw = Array.isArray(debtResult)
    ? (debtResult[1]?.toString() ?? '0')
    : '0'

  // Total debt = drawn + premium (premium is the risk-adjusted portion)
  const totalDebtRaw = (
    BigInt(drawnDebtRaw || '0') + BigInt(premiumDebtRaw || '0')
  ).toString()

  if ((suppliedRaw === '0' || !suppliedRaw) && totalDebtRaw === '0') {
    return {
      dataForAsset: undefined,
      addedDeposits: 0,
      addedDebt: 0,
    }
  }

  const assetMeta = meta.asset
  const decimals = assetMeta?.decimals ?? 18

  const currentDeposits = parseRawAmount(suppliedRaw, decimals)
  const currentDebt = parseRawAmount(totalDebtRaw, decimals)

  // getUserReserveStatus returns (usingAsCollateral, isBorrowing)
  const collateralEnabled = Boolean(statusResult?.[0])

  // prices — use config-scoped oracle price for the user's spoke
  const price = getDisplayPrice(meta)
  const oPrice = getConfigOraclePrice(meta, spokeAddr)
  const priceHist = meta?.price?.priceUsd24h ?? price

  const dataForAsset = {
    marketUid: key,
    underlying: assetMeta.address,
    deposits: currentDeposits,
    debtStable: '0',
    debt: currentDebt,
    depositsUSD: Number(currentDeposits) * price,
    debtStableUSD: 0,
    debtUSD: Number(currentDebt) * price,
    depositsUSDOracle: Number(currentDeposits) * oPrice,
    debtStableUSDOracle: 0,
    debtUSDOracle: Number(currentDebt) * oPrice,
    stableBorrowRate: 0,
    collateralEnabled,
    claimableRewards: 0,
  }

  return {
    dataForAsset,
    addedDeposits: Number(currentDeposits) * priceHist,
    addedDebt: Number(currentDebt) * priceHist,
  }
}
